import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Image as ImageIcon, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { hasPermission } from "../lib/utils/permissions";
import { useAuthStore } from "../context/AuthContext";
import useToast from "../hooks/useToast";
import apiClient from "../lib/utils/network-client";
import { DeleteConfirmationButton } from "../components/common/ConfirmationDialog";
import AdminListToolbar from "../components/common/AdminListToolbar";
import ListPagination from "../components/common/ListPagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";


const Facilities = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { showErrorToast, showSuccessToast } = useToast();
  const canCreate = hasPermission(user, "facilities", "create");
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [imageFilter, setImageFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    return items.filter((item) => {
      const hasImage = Boolean(item.image_url);
      const matchesImage = imageFilter === "all"
        || (imageFilter === "with-image" && hasImage)
        || (imageFilter === "without-image" && !hasImage);
      const matchesSearch = !query || String(item.title || "").toLowerCase().includes(query);
      return matchesImage && matchesSearch;
    });
  }, [imageFilter, items, search]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const paginatedItems = filteredItems.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    setPage((currentPage) => Math.min(currentPage, totalPages));
  }, [totalPages]);

  const fetchItems = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get("/facilities");
      setItems(response.data?.data?.facilities || []);
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to load facilities");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleDelete = async (item) => {
    try {
      await apiClient.delete(`/facilities/${item.id}`);
      showSuccessToast("Facility deleted successfully");
      fetchItems();
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Unable to delete facility");
    }
  };

  return (
    <div className="max-w-[1600px] space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Facilities</h1>
          <p className="mt-1 text-sm font-medium text-slate-500">Manage facility title and image cards.</p>
        </div>
        <div className="flex gap-3">
          <Button type="button" variant="outline" className="rounded-lg" onClick={fetchItems}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          {canCreate && (

            <Button type="button" className="rounded-lg" onClick={() => navigate("/facilities/new")}>
            <Plus className="mr-2 h-4 w-4" />
            Add Facility
          </Button>

          )}
        </div>
      </div>

      <AdminListToolbar
        searchValue={search}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(1);
        }}
        searchPlaceholder="Search facility title..."
        searchLabel="Search facilities"
        filteredCount={filteredItems.length}
        totalCount={items.length}
        itemLabel="facilities"
        hasActiveFilters={Boolean(search) || imageFilter !== "all"}
        onReset={() => {
          setSearch("");
          setImageFilter("all");
          setPage(1);
        }}
      >
        <Select value={imageFilter} onValueChange={(value) => {
          setImageFilter(value);
          setPage(1);
        }}>
          <SelectTrigger className="min-w-44" aria-label="Filter facilities by image status">
            <SelectValue placeholder="Image status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All image statuses</SelectItem>
            <SelectItem value="with-image">With image</SelectItem>
            <SelectItem value="without-image">Without image</SelectItem>
          </SelectContent>
        </Select>
      </AdminListToolbar>

      <div className="rounded-lg border border-white/60 bg-white/80 p-6 shadow-sm backdrop-blur-xl">
        {isLoading ? (
          <div className="py-12 text-center text-sm font-medium text-slate-500">Loading facilities...</div>
        ) : filteredItems.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-500">
            {items.length === 0 ? "No facilities found." : "No facilities match your filters."}
          </div>
        ) : (
          <>
          <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
            {paginatedItems.map((item) => (
              <div key={item.id} className="overflow-hidden rounded-lg border border-slate-100 bg-white shadow-sm">
                <div className="grid gap-0 md:grid-cols-[220px_1fr]">
                  <div className="bg-slate-100">
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.title} className="h-full min-h-56 w-full object-cover" />
                    ) : (
                      <div className="flex h-full min-h-56 items-center justify-center text-slate-400">
                        <ImageIcon className="h-8 w-8" />
                      </div>
                    )}
                  </div>

                  <div className="space-y-4 p-5">
                    <div>
                      <h2 className="text-lg font-bold text-slate-800">{item.title}</h2>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="outline" className="rounded-lg" onClick={() => navigate(`/facilities/${item.id}/edit`)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        Edit
                      </Button>
                      <DeleteConfirmationButton onConfirm={() => handleDelete(item)}>
<Button type="button" variant="outline" className="rounded-lg border-red-200 text-red-600 hover:bg-red-50">
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </Button>
</DeleteConfirmationButton>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="-mx-6 -mb-6 mt-6 overflow-hidden rounded-b-lg">
            <ListPagination
              page={page}
              pageSize={pageSize}
              totalItems={filteredItems.length}
              onPageChange={setPage}
              onPageSizeChange={(value) => {
                setPageSize(value);
                setPage(1);
              }}
              itemLabel="facilities"
            />
          </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Facilities;
